import { Key, URI, unwrapNatural, unwrapPositive, validNatural, validPositive } from './std';


import { AnnotationLabel, CommitCount, ContributionDate, EndDate, MetricLabel, MetricValue, Position, PrCount, ReviewCount, ShortDescription, StartDate, StoryRole, TagName, Title, Url, User, unwrapCommitCount, unwrapPosition, unwrapPrCount, unwrapReviewCount, unwrapTagName, unwrapTitle, unwrapUrl } from './runtime';


import { Repo } from './repo';


export type SocialPlatform = "website" | "mastodon" | "linkedin" | "blog" | "contactEmail" | "github" | "gitlab" | "twitter"

export const validSocialPlatform = (x: unknown): SocialPlatform => {
  const v = x as [string, unknown] | string;
  if(((v != 'website')) && ((v != 'mastodon')) && ((v != 'linkedin')) && ((v != 'blog')) && ((v != 'contactEmail')) && ((v != 'github')) && ((v != 'gitlab')) && ((v != 'twitter'))) throw new Error('Invalid SocialPlatform `' + JSON.stringify(x) + '`');
  return x as SocialPlatform;
}

export type ProjectStoryStatus = "active" | "archived" | "completed"

export const validProjectStoryStatus = (x: unknown): ProjectStoryStatus => {
  const v = x as [string, unknown] | string;
  if(((v != 'active')) && ((v != 'archived')) && ((v != 'completed'))) throw new Error('Invalid ProjectStoryStatus `' + JSON.stringify(x) + '`');
  return x as ProjectStoryStatus;
}

export interface UserSocialLink {
  user: Key<User>;
  platform: SocialPlatform;
  url: Url;
  position: Position | null;
}

export const validUserSocialLink = (input: unknown): UserSocialLink => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["platform"] === undefined) throw new Error('`_x["platform"]` required but not present');
  validSocialPlatform(_x["platform"] as any);
  if (_x["url"] === undefined) throw new Error('`_x["url"]` required but not present');
  
  if (_x["position"] === undefined) throw new Error('`_x["position"]` required but not present');
  _x["position"] === null || validPositive(unwrapPositive(unwrapPosition(_x["position"] as any)) as any);
  const x = input as UserSocialLink;
  return x;
}
export const isValidUserSocialLink = (input: unknown): boolean => {
  try { validUserSocialLink(input); return true; } catch { return false; }
}

export interface PinnedRepo {
  user: Key<User>;
  repo: Key<Repo>;
  position: Position;
}

export interface UserFollow {
  follower: Key<User>;
  followed: Key<User>;
}

export const validUserFollow = (input: unknown): UserFollow => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["follower"] === undefined) throw new Error('`_x["follower"]` required but not present');
  
  if (_x["followed"] === undefined) throw new Error('`_x["followed"]` required but not present');
  
  const x = input as UserFollow;
  return x;
}
export const isValidUserFollow = (input: unknown): boolean => {
  try { validUserFollow(input); return true; } catch { return false; }
}

export interface Contribution {
  user: Key<User>;
  date: ContributionDate;
  commitCount: CommitCount;
  prCount: PrCount;
  reviewCount: ReviewCount;
}

export const validContribution = (input: unknown): Contribution => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["date"] === undefined) throw new Error('`_x["date"]` required but not present');
  
  if (_x["commitCount"] === undefined) throw new Error('`_x["commitCount"]` required but not present');
  validNatural(unwrapNatural(unwrapCommitCount(_x["commitCount"] as any)) as any);
  if (_x["prCount"] === undefined) throw new Error('`_x["prCount"]` required but not present');
  validNatural(unwrapNatural(unwrapPrCount(_x["prCount"] as any)) as any);
  if (_x["reviewCount"] === undefined) throw new Error('`_x["reviewCount"]` required but not present');
  validNatural(unwrapNatural(unwrapReviewCount(_x["reviewCount"] as any)) as any);
  const x = input as Contribution;
  return x;
}
export const isValidContribution = (input: unknown): boolean => {
  try { validContribution(input); return true; } catch { return false; }
}

export type GridCellKind = "review" | "issue" | "docs" | "code"

export const validGridCellKind = (x: unknown): GridCellKind => {
  const v = x as [string, unknown] | string;
  if(((v != 'review')) && ((v != 'issue')) && ((v != 'docs')) && ((v != 'code'))) throw new Error('Invalid GridCellKind `' + JSON.stringify(x) + '`');
  return x as GridCellKind;
}

export interface GridCell {
  week: number;
  day: number;
  count: number;
  level: number;
  color: string;
  kind: GridCellKind;
  date: string;
  tooltip: string;
}

export interface SkillTag {
  name: TagName;
}

export const validSkillTag = (input: unknown): SkillTag => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  
  const x = input as SkillTag;
  return x;
}
export const isValidSkillTag = (input: unknown): boolean => {
  try { validSkillTag(input); return true; } catch { return false; }
}

export interface UserSkill {
  user: Key<User>;
  skillTag: Key<SkillTag>;
  position: Position | null;
}

export const validUserSkill = (input: unknown): UserSkill => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["skillTag"] === undefined) throw new Error('`_x["skillTag"]` required but not present');
  
  if (_x["position"] === undefined) throw new Error('`_x["position"]` required but not present');
  _x["position"] === null || validPositive(unwrapPositive(unwrapPosition(_x["position"] as any)) as any);
  const x = input as UserSkill;
  return x;
}
export const isValidUserSkill = (input: unknown): boolean => {
  try { validUserSkill(input); return true; } catch { return false; }
}

export interface ProjectStory {
  user: Key<User>;
  title: Title;
  description: ShortDescription;
  role: StoryRole;
  repoUrl: Url | null;
  coverImageUrl: Url | null;
  status: ProjectStoryStatus;
  metricValue: MetricValue | null;
  metricLabel: MetricLabel | null;
  position: Position;
}

export const validProjectStory = (input: unknown): ProjectStory => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["title"] === undefined) throw new Error('`_x["title"]` required but not present');
  
  if (_x["description"] === undefined) throw new Error('`_x["description"]` required but not present');
  
  if (_x["role"] === undefined) throw new Error('`_x["role"]` required but not present');
  
  if (_x["repoUrl"] === undefined) throw new Error('`_x["repoUrl"]` required but not present');
  
  if (_x["coverImageUrl"] === undefined) throw new Error('`_x["coverImageUrl"]` required but not present');
  
  if (_x["status"] === undefined) throw new Error('`_x["status"]` required but not present');
  validProjectStoryStatus(_x["status"] as any);
  if (_x["metricValue"] === undefined) throw new Error('`_x["metricValue"]` required but not present');
  
  if (_x["metricLabel"] === undefined) throw new Error('`_x["metricLabel"]` required but not present');
  
  if (_x["position"] === undefined) throw new Error('`_x["position"]` required but not present');
  validPositive(unwrapPositive(unwrapPosition(_x["position"] as any)) as any);
  const x = input as ProjectStory;
  return x;
}
export const isValidProjectStory = (input: unknown): boolean => {
  try { validProjectStory(input); return true; } catch { return false; }
}

export interface ProjectStoryTag {
  projectStory: Key<ProjectStory>;
  tag: Key<SkillTag>;
}

export const validProjectStoryTag = (input: unknown): ProjectStoryTag => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["projectStory"] === undefined) throw new Error('`_x["projectStory"]` required but not present');
  
  if (_x["tag"] === undefined) throw new Error('`_x["tag"]` required but not present');
  
  const x = input as ProjectStoryTag;
  return x;
}
export const isValidProjectStoryTag = (input: unknown): boolean => {
  try { validProjectStoryTag(input); return true; } catch { return false; }
}

export interface ActivityAnnotation {
  user: Key<User>;
  startDate: StartDate;
  endDate: EndDate;
  label: AnnotationLabel;
}

export const validActivityAnnotation = (input: unknown): ActivityAnnotation => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["startDate"] === undefined) throw new Error('`_x["startDate"]` required but not present');
  
  if (_x["endDate"] === undefined) throw new Error('`_x["endDate"]` required but not present');
  
  if (_x["label"] === undefined) throw new Error('`_x["label"]` required but not present');
  
  const x = input as ActivityAnnotation;
  return x;
}
export const isValidActivityAnnotation = (input: unknown): boolean => {
  try { validActivityAnnotation(input); return true; } catch { return false; }
}

export const illuminateSocialPlatform = (__x: SocialPlatform): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "website": {
  return "Website";
  }
  case "mastodon": {
  return "Mastodon";
  }
  case "linkedin": {
  return "LinkedIn";
  }
  case "blog": {
  return "Blog";
  }
  case "contactEmail": {
  return "Email";
  }
  case "github": {
  return "GitHub";
  }
  case "gitlab": {
  return "GitLab";
  }
  case "twitter": {
  return "Twitter";
  }
  }
}

export const illuminateProjectStoryStatus = (__x: ProjectStoryStatus): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "active": {
  return "Active";
  }
  case "archived": {
  return "Archived";
  }
  case "completed": {
  return "Completed";
  }
  }
}

export const illuminateUserSocialLink = (x: UserSocialLink): string => unwrapUrl(x.url)

export const illuminatePinnedRepo = (x: PinnedRepo): string => String(unwrapNatural(unwrapPositive(unwrapPosition(x.position))))

export const validPinnedRepo = (input: unknown): PinnedRepo => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["repo"] === undefined) throw new Error('`_x["repo"]` required but not present');
  
  if (_x["position"] === undefined) throw new Error('`_x["position"]` required but not present');
  validPositive(unwrapPositive(unwrapPosition(_x["position"] as any)) as any);
  const x = input as PinnedRepo;
  const errors: string[] = [];
  if ((unwrapNatural(unwrapPositive(unwrapPosition(x.position)))) > 6) errors.push(`Cannot pin more than 6 repos`);
  if (errors.length > 0) throw new Error(errors.join('; '));
  return x;
}
export const isValidPinnedRepo = (input: unknown): boolean => {
  try { validPinnedRepo(input); return true; } catch { return false; }
}

export const validGridCell = (input: unknown): GridCell => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["week"] === undefined) throw new Error('`_x["week"]` required but not present');
  if (typeof _x["week"] !== 'number' || !Number.isSafeInteger(_x["week"] as number)) throw new Error('`_x["week"]` must be a safe integer');
  if (_x["day"] === undefined) throw new Error('`_x["day"]` required but not present');
  if (typeof _x["day"] !== 'number' || !Number.isSafeInteger(_x["day"] as number)) throw new Error('`_x["day"]` must be a safe integer');
  if (_x["count"] === undefined) throw new Error('`_x["count"]` required but not present');
  if (typeof _x["count"] !== 'number' || !Number.isSafeInteger(_x["count"] as number)) throw new Error('`_x["count"]` must be a safe integer');
  if (_x["level"] === undefined) throw new Error('`_x["level"]` required but not present');
  if (typeof _x["level"] !== 'number' || !Number.isSafeInteger(_x["level"] as number)) throw new Error('`_x["level"]` must be a safe integer');
  if (_x["color"] === undefined) throw new Error('`_x["color"]` required but not present');
  if (typeof _x["color"] != 'string') throw new Error('`_x["color"]` must be a string');
  if (_x["kind"] === undefined) throw new Error('`_x["kind"]` required but not present');
  validGridCellKind(_x["kind"] as any);
  if (_x["date"] === undefined) throw new Error('`_x["date"]` required but not present');
  if (typeof _x["date"] != 'string') throw new Error('`_x["date"]` must be a string');
  if (_x["tooltip"] === undefined) throw new Error('`_x["tooltip"]` required but not present');
  if (typeof _x["tooltip"] != 'string') throw new Error('`_x["tooltip"]` must be a string');
  const x = input as GridCell;
  const errors: string[] = [];
  if ((x.week) < 0) errors.push(`week index must be non-negative`);
  if ((x.day) < 0) errors.push(`day index must be in 0..6`);
  if ((x.level) < 0) errors.push(`level must be in 0..4`);
  if ((x.count) < 0) errors.push(`count must be non-negative`);
  if ((x.day) > 6) errors.push(`day index must be in 0..6`);
  if ((x.level) > 4) errors.push(`level must be in 0..4`);
  if (errors.length > 0) throw new Error(errors.join('; '));
  return x;
}
export const isValidGridCell = (input: unknown): boolean => {
  try { validGridCell(input); return true; } catch { return false; }
}

export const illuminateSkillTag = (x: SkillTag): string => unwrapTagName(x.name)

export const illuminateProjectStory = (x: ProjectStory): string => unwrapTitle(x.title)

export const allSocialPlatforms: Set<SocialPlatform> =
  new Set(['website', 'mastodon', 'linkedin', 'blog', 'contactEmail', 'github', 'gitlab', 'twitter'])


export const socialPlatformGlyph = (platform: SocialPlatform): string => {
  return (() => {switch (typeof platform === 'string' ? platform : (platform as [string, unknown])[0]) {
      case "website": {
        return "f0ac";
      }
      case "mastodon": {
        return "f4f6";
      }
      case "linkedin": {
        return "f08c";
      }
      case "blog": {
        return "f1ea";
      }
      case "contactEmail": {
        return "f0e0";
      }
      case "github": {
        return "f09b";
      }
      case "gitlab": {
        return "f296";
      }
      case "twitter": {
        return "f099";
      }
    default: throw new Error('unreachable');
    }})()
}

export const allProjectStoryStatuses: Set<ProjectStoryStatus> =
  new Set(['active', 'archived', 'completed'])


export const projectStoryStatusBadgeClass = (status: ProjectStoryStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "active": {
        return "bg-green/15 text-green border border-green/30";
      }
      case "completed": {
        return "bg-accent/15 text-accent border border-accent/30";
      }
      case "archived": {
        return "bg-surface text-text-muted border border-border";
      }
    default: throw new Error('unreachable');
    }})()
}

export const allGridCellKinds: Set<GridCellKind> =
  new Set(['review', 'issue', 'docs', 'code'])


export const createUserSocialLink: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-social-links",
    'query': null,
    'fragment': null,
    'io': 'UserSocialLink. UserSocialLink',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listUserSocialLinks: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-social-links",
    'query': null,
    'fragment': null,
    'io': 'Unit. [UserSocialLink]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createPinnedRepo: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/pinned-repos",
    'query': null,
    'fragment': null,
    'io': 'PinnedRepo. PinnedRepo',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listPinnedRepos: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/pinned-repos",
    'query': null,
    'fragment': null,
    'io': 'Unit. [PinnedRepo]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createUserFollow: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-follows",
    'query': null,
    'fragment': null,
    'io': 'UserFollow. UserFollow',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listUserFollows: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-follows",
    'query': null,
    'fragment': null,
    'io': 'Unit. [UserFollow]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createContribution: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/contributions",
    'query': null,
    'fragment': null,
    'io': 'Contribution. Contribution',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listContributions: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/contributions",
    'query': null,
    'fragment': null,
    'io': 'Unit. [Contribution]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createSkillTag: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/skill-tags",
    'query': null,
    'fragment': null,
    'io': 'SkillTag. SkillTag',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listSkillTags: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/skill-tags",
    'query': null,
    'fragment': null,
    'io': 'Unit. [SkillTag]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createUserSkill: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-skills",
    'query': null,
    'fragment': null,
    'io': 'UserSkill. UserSkill',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listUserSkills: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/user-skills",
    'query': null,
    'fragment': null,
    'io': 'Unit. [UserSkill]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createProjectStory: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/project-stories",
    'query': null,
    'fragment': null,
    'io': 'ProjectStory. ProjectStory',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listProjectStories: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/project-stories",
    'query': null,
    'fragment': null,
    'io': 'Unit. [ProjectStory]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createProjectStoryTag: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/project-story-tags",
    'query': null,
    'fragment': null,
    'io': 'ProjectStoryTag. ProjectStoryTag',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listProjectStoryTags: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/project-story-tags",
    'query': null,
    'fragment': null,
    'io': 'Unit. [ProjectStoryTag]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createActivityAnnotation: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/activity-annotations",
    'query': null,
    'fragment': null,
    'io': 'ActivityAnnotation. ActivityAnnotation',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listActivityAnnotations: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/activity-annotations",
    'query': null,
    'fragment': null,
    'io': 'Unit. [ActivityAnnotation]',
    'meta': new Set(["GET"]),
    'accept': null,
  }

//# sourceMappingURL=profile.ts.map
